import { forEach, forOwn, map, keys, pick } from 'lodash';

import { Base, moveTo, lineTo, setLineDash, strokeSegment } from 'gear';

import { Point, Matrix, Frame } from 'cdl';

import { padScaling, fastThrottle } from '../view';

const { distance2 } = Point;

function maxSideLength2( frame ) {
  const { p0, p1, p2, p3 } = frame;
  return Math.max( distance2(p1,p0), distance2(p2,p1), distance2(p3,p2), distance2(p0,p3) );
}

function minSideLength2( frame ) {
  const { p0, p1, p2, p3 } = frame;
  return Math.min( distance2(p1,p0), distance2(p2,p1), distance2(p3,p2), distance2(p0,p3) );
}

export /* default */ let SelectHandles = Base.extend({

  Properties: {
    view : { type: 'View', def: null, owned: false },

    anisometricTransformsEnabled : { type: 'boolean', def: true },
    translateEnabled             : { type: 'boolean', def: true },
    scaleEnabled                 : { type: 'boolean', def: true },
    rotateEnabled                : { type: 'boolean', def: true },
    shearEnabled                 : { type: 'boolean', def: true },
    editEnabled                  : { type: 'boolean', def: false }
  },
  init() {
    this._visible = false;

    this.guideline_x = null;
    this.guideline_y = null;

    this.mouseDownPos = new Point(0,0);

    this._handles = {
      center : { mode: 'translate' },
      nw     : { mode: 'scale'     },
      ne     : { mode: 'scale'     },
      se     : { mode: 'scale'     },
      sw     : { mode: 'scale'     },
      n      : { mode: 'scale'     , aniso: true },
      e      : { mode: 'scale'     , aniso: true },
      s      : { mode: 'scale'     , aniso: true },
      w      : { mode: 'scale'     , aniso: true },
      rotate : { mode: 'rotate'    },
      shearX : { mode: 'shear'     , aniso: true },
      shearY : { mode: 'shear'     , aniso: true },
      edit   : { mode: 'edit'      },
      none   : { mode: 'none'      }
    };

    const size = 0.1 * 96;

    const hs = this._handles;
    forEach( hs, (h,hn) => {
      h.name = hn; h.size = size; h.cursor = 'pointer'; h.disabled=false;
    });
    hs.rotate.size = hs.edit.size = size;

    hs.center.cursor = 'move';
    hs.none.cursor = 'default';

    forEach( hs, h => {
      h.size2 = h.size*h.size+1;
    });

    hs.nw.pin = hs.se; hs.ne.pin = hs.sw;
    hs.se.pin = hs.nw; hs.sw.pin = hs.ne;
    hs.n .pin = hs.s ; hs.e .pin = hs.w ;
    hs.s .pin = hs.n ; hs.w .pin = hs.e ;
    hs.shearX.pin = hs.n; hs.shearX.oppPin = hs.s;
    hs.shearY.pin = hs.w; hs.shearY.oppPin = hs.w;

    this._circlePadHandles   = pick(hs,'rotate','nw','ne','se','sw','n','e','s','w','shearX','shearY','edit');

    this._fillColor       = '#22CC33';
    this._fillOverColor   = '#006633';
    this._fillActiveColor = '#fb603d';
    this._strokeColor     = '#006633';
    this._guidelineColor  = '#993300';

    this.addEvents(    'rotatestart',    'rotatemove',    'rotateend' ,
                        'scalestart',     'scalemove',     'scaleend' ,
                    'translatestart', 'translatemove', 'translateend' ,
                        'shearstart',     'shearmove',     'shearend' , 'edit');

    this._currentActive = this._handles.none;
    this._currentOver   = this._handles.none;
    this.current = this._handles.none;
    this._setFrame( Frame.fromBounds({lx: 100, ly: 100, hx: 200, hy: 200 }) );
    this._inited = false;
    this.enable(true);
  },

  _rotateHandlePosition( p1, p2, distance ) {
    const de = p2.minus(p1) .normalized();
    return p2.plus( de.scaled( this.view.viewDeltaToDoc(distance) ) );
  },

  _editHandlePosition( p1, p2, distance ) {
    const de = p2.minus(p1) .normalized();
    return p2.plus( de.scaled( this.view.viewDeltaToDoc(distance) ) );
  },

  _setFrame( frame ) {
    this._frame = frame;
    this._inited = true;
    const interpolate = Point.interpolate;
    const nw = this._frame.p0, ne = this._frame.p1, se = this._frame.p2, sw = this._frame.p3;
    const p = {
      nw: nw, ne: ne, se: se, sw: sw,
      n: interpolate(nw,ne,0.5),
      e: interpolate(ne,se,0.5),
      s: interpolate(se,sw,0.5),
      w: interpolate(sw,nw,0.5)
    };

    p.shearX = interpolate(p.s,se,0.5);
    p.shearY = interpolate(se,p.e,0.5);

    p.rotate = this._rotateHandlePosition(p.w,p.e,20);
    p.edit = this._editHandlePosition(p.e,p.w,20);

    const hs = this._handles;
    forOwn( p, (pos,hn) => {
      hs[hn].pos = pos;
    });
  },

// frame: The figure frame in the View coordinates.
  resize( frame ) {

    const { view } = this;
    const frameMinSideLength2 = minSideLength2(frame);
          //frameMaxSideLength2 = maxSideLength2(frame);

    const vtds = view.viewDeltaToDoc(1), vtds2 = vtds*vtds;

    const smallFrame = frameMinSideLength2 < 45*45 *vtds2;

    if( smallFrame ) {
      const ex = 12 * vtds;
      // console.log(vtds);
      frame = frame.extended(ex,ex,ex,ex);
      //this._anisometricTransformsEnabled = false;
    }

    // const ps = view.viewDeltaToDoc(padScaling);

    // const hs = this._handles;
    // const { width, height } = frame;
    // const shearCut1 = 40 * ps,
    //       shearCut2 = 60 * ps,
    //       scaleCut1 = 20 * ps,
    //       scaleCut2 = 30 * ps;
    // hs.shearX.disabled = height < shearCut1 || width < shearCut2;
    // hs.shearY.disabled = height < shearCut2 || width < shearCut1;
    // hs.e.disabled = width < scaleCut1 || height < scaleCut2;
    // hs.w.disabled = width < scaleCut1 || height < scaleCut2;
    // hs.n.disabled = width < scaleCut2 || height < scaleCut1;
    // hs.s.disabled = width < scaleCut2 || height < scaleCut1;
    this.update(frame);
  },

  update( frame ) {
    this._setFrame( frame );
  },

  enable( isEnabled ) {
    if( isEnabled === undefined ) {
      this.enabled = true ;
    }
    else {
      this.enabled = isEnabled ;
    }
  },
  isEnabled() {
    return this.enabled ;
  },

  handleEnabled( c ) {
    return c.mode === 'none' || ( this[ c.mode + 'Enabled' ] && ( this._anisometricTransformsEnabled || ! c.aniso ) );
  },

  down({ event, docPoint }) {
    if( ! this.isEnabled() ) {
      return ;
    }

    const c = this._handleFromEvent(event);

    if( ! this.handleEnabled(c) ) {
      return;
    }
    this.current = c;

    if( c.mode === 'none' ) {
      return;
    }
    else if( c.mode === 'edit' ) {
      this.fireEvent('edit', event);
      this.current = this._handles.none;
      return;
    }

    this._currentActive = this.current;

    this._currentOver = this._handles.none;

    this.mouseDownPos = docPoint;

    this.fireEvent(this.current.mode + 'start', event); // shear, rotate, scale, or translate
  },

  mousemove({ view, event }) {

    if( view.is3D ) {
      // Ignore feedback for 3D views
      return;
    }

    // Handle pad Over
    if( this._visible && this.current.mode === 'none' ) {
      const newOver = this._handleFromEvent(event);
      if( this._currentOver !== newOver ) {

        this._currentOver = newOver;

        if( newOver.mode === 'none' || this.handleEnabled(newOver) ) {
          view.cursor = newOver.cursor;
        }

        view.redraw('tools');

        event.stopPropagation();
      }
    }
  },

  move: fastThrottle( function({ view, event }) {

    if( this.current.mode === 'none' ) {
      return;
    }
    this.fireEvent(this.current.mode + 'move', event); // rotate, scale, select, shear or translate
  }, 12 ),

  up({ view, event }) {
    const mode = this.current.mode;
    if( mode === 'none' ) {
      return;
    }

    this._currentActive = this._handles.none;

    this.fireEvent(mode + 'end', event); // rotate, scale, select, or translate

    this.current = this._handles.none;

    if( mode !== 'translate' ) { // avoid glitch
      view.cursor = this.current.cursor;
    }
  },

  shearDirFromHandle( h ) {
    const hs = this._handles;
    switch( h.name ) {
      case 'shearX':
        return hs.e.pos.minus(hs.w.pos) .normalized();
      case 'shearY':
        return hs.n.pos.minus(hs.s.pos) .normalized();
     }
  },

  scaleDirFromHandle( h, transform = Matrix() ) {
    return transform.apply(h.pos).minus( transform.apply(h.pin.pos) ) .normalized();
  },

  strokeFrame( context, transform ) {
    const hs = this._handles;

    // rotate arm
    if( this.rotateEnabled ) {
      strokeSegment(context,hs.e.pos,this._rotateHandlePosition(hs.w.pos,hs.e.pos,20),transform);
    }

    // Center handle
    this._frame.draw(context,transform);
    context.stroke();
  },

  draw({ view, context, transform }) {

    if( this._inited && this._visible ) {

      context.save();

      const hs = this._handles;

      const textureScale = view.viewDeltaToTexture(1);

      setLineDash(context, map( [5,2], v => textureScale*v ) );
      context.lineWidth = 2.4 * textureScale;
      const strokeStyle = context.strokeStyle = '#e3e3e3';
      this.strokeFrame(context,transform);

      setLineDash(context, map( [0,0.5,4,2.5], v => textureScale*v ) );
      context.lineWidth = 1.2 * textureScale;
      context.strokeStyle = '#388638';
      this.strokeFrame(context,transform);

      context.strokeStyle = strokeStyle;
      context.lineWidth = 1 * textureScale; // [TODO]
      setLineDash(context,[]);

      if( this.scaleEnabled ) {
        forEach(['nw','ne','se','sw'], hn => {
          const h = hs[hn];
          this._drawScaleHandle(context,h,transform,textureScale);
          context.stroke();
          context.fillStyle = this.fillStyle(h);
          context.fill();
        });
      }

      if( this.rotateEnabled ) {
        // Rotate handle, raphael was taking a lot of time to rotate the handle
        const rotateHandle = hs.rotate;
        context.beginPath();

        // [TODO] Clean up, running to release
        rotateHandle.pos = this._rotateHandlePosition(hs.w.pos,hs.e.pos,28);

        const rotationPos = transform.apply(rotateHandle.pos);
        const rotationAngle = transform.apply(hs.e.pos).minus( transform.apply(hs.w.pos) ) .angle();
        const rotationPathTransform = Matrix.concat( Matrix.translate(rotationPos),
                                                   Matrix.scale( 0.65 * textureScale ),
                                                   Matrix.rotate(rotationAngle) );
        canvasRotationPath(context,rotationPathTransform);
        context.stroke();
        context.fillStyle = this.fillStyle(rotateHandle);
        context.fill();
      }

      if( this.editEnabled ) {
        const editHandle = hs.edit;
        context.beginPath();

        // [TODO] Clean up, running to release
        editHandle.pos = this._editHandlePosition(hs.e.pos,hs.w.pos,20);

        const editAngle = transform.apply(hs.e.pos) .minus( transform.apply(hs.w.pos) ) .angle();
        canvasEditContentPath( context, Matrix.concat(
          Matrix.translate( transform.apply(editHandle.pos) ),
          Matrix.scale( 0.6 * textureScale ),
          Matrix.rotate(editAngle),
          Matrix.translate(-16,-16)
        ));
        context.stroke();
        context.fillStyle = this.fillStyle(editHandle);
        context.fill();
      }

      if( this._anisometricTransformsEnabled ) {

        if( this.scaleEnabled ) {
          forEach(['n','e','s','w'], hn => {
            const h = hs[hn];
            if( ! h.disabled ) {
              this._drawScaleHandle(context,h,transform,textureScale);
              context.stroke();
              context.fillStyle = this.fillStyle(h);
              context.fill();
            }
          });
        }

        if( this.shearEnabled ) {
          forEach(['shearX','shearY'], hn => {
            const h = hs[hn];
            if( ! h.disabled ) {

              const tp = transform.apply(h.pos);
              context.beginPath(); context.arc( tp.x, tp.y, 3.5*textureScale, 0,2*Math.PI); // [TODO] dot
              context.stroke();
              context.fillStyle = this.fillStyle(h);
              context.fill();
            }
          });
        }
      }

      // Guidelines

      const bounds = view.bounds;
      context.strokeStyle = this._guidelineColor;
      context.lineWidth = 0.75;
      setLineDash(context,[3,2]);

      const x = this.guideline_x;
      if( x !== null ) {
        strokeSegment(context,new Point(x,bounds.ly),new Point(x,bounds.hy),transform);
      }

      const y = this.guideline_y;
      if( y !== null) {
        strokeSegment(context,new Point(bounds.lx,y),new Point(bounds.hx,y),transform);
      }

      context.restore();
    }
  },

  fillStyle( h ) {
    return h === this._currentActive ? this._fillActiveColor
         : h === this._currentOver   ? this._fillOverColor
                                     : this._fillColor ;
  },

  isHandle( event ) {
    return this._handleFromEvent(event).name !== 'none';
  },

  isPadHandle( event ) {
    const n = this._handleFromEvent(event).name;
    return n !== 'none' && n !== 'center';
  },

  isHandleEnabledAt( event ) {
    return this.handleEnabled( this._handleFromEvent(event) );
  },

  _handleFromEvent({ event, docPoint }) {
    const view = this.view;
    const hs = this._handles;
    if( this._visible ) {
      const point = docPoint;
      const ps = view.viewDeltaToDoc(padScaling), ps2 = ps*ps; // [TODO]

      const padHandles = keys(this._circlePadHandles);
      for( let k = 0, kEnd = padHandles.length; k < kEnd; ++k ) {
        const hk = hs[padHandles[k]];
        if( this.handleEnabled(hk) && ! hk.disabled &&
            hk.pos.minus(point) .norm2() < hk.size2 * ps2 ) {
          return hk;
        }
      }

      if( this._frame.contains(point) ) {
        return hs.center;
      }
    }
    return hs.none;
  },

  // Returns center point in View coordinates.
  get center() {
    return this._frame.center;
  },

  get visible() {
    return this._visible;
  },
  set visible( visible_ ) {
    this._visible = visible_;
  },

  _drawScaleHandle( context, h, transform, textureScale ) {
    const scale = 4.5;
    const dir  = this.scaleDirFromHandle(h,transform);
    const dirp = dir.perpendicular( -scale * textureScale );
    const p    = transform.apply(h.pos).plus( dir.scaled( 0.3 * scale * textureScale ) );
    const t1  =  p.plus( dir.scaled( scale * textureScale ) );
    const tb  =  p.plus( dir.scaled( -scale * textureScale ) );
    const t2  = tb.plus( dirp.scaled( -scale * textureScale ) );
    const t3  = tb.plus( dirp.scaled( scale * textureScale ) );

    context.beginPath();
    moveTo(context,t1);
    lineTo(context,t2);
    lineTo(context,t3);
    context.closePath();
  }
});

export default SelectHandles;


// Handle paths, we need to find a better way, this is here since we moved
// away from Raphael. It was unrolled thinking on performance but it is
// probably not making us gain much

const moveToXY = function( c,t, x,y ) {
  const p = t.apply_(x,y);
  c.moveTo( p.x,p.y );
};

const lineToXY = function( c,t, x,y ) {
  const p = t.apply_(x,y);
  c.lineTo( p.x,p.y );
};

const bezierCurveTo = function(c,t,p0_x,p0_y,p1_x,p1_y,p2_x,p2_y) {
  const p0 = t.apply_(p0_x,p0_y),
      p1 = t.apply_(p1_x,p1_y),
      p2 = t.apply_(p2_x,p2_y);
  c.bezierCurveTo( p0.x,p0.y, p1.x,p1.y, p2.x,p2.y );
};

function canvasRotationPath(c,t) {
  moveToXY(c,t,-8.084,0.500);
  bezierCurveTo(c,t,  -8.075,5.240,  -4.240,9.074,  0.499,9.083);
  bezierCurveTo(c,t,  5.240,9.074,  9.076,5.239,  9.084,0.500);
  bezierCurveTo(c,t,  9.076,-4.241, 5.240,-8.077, 0.499,-8.085);
  bezierCurveTo(c,t,  -1.414,-8.085, -3.166,-7.456, -4.591,-6.399);
  bezierCurveTo(c,t,  -4.591,-6.399,  -2.809,-4.616, -2.809,-4.616);
  bezierCurveTo(c,t,  -2.809,-4.616, -11.238,-2.360, -11.238,-2.360);
  bezierCurveTo(c,t,  -11.238,-2.360,  -8.978,-10.787, -8.978,-10.787);
  bezierCurveTo(c,t,  -8.978,-10.787,  -7.088,-8.897,  -7.088,-8.897);
  bezierCurveTo(c,t,  -5.016,-10.574,  -2.371,-11.585,  0.499,-11.585);
  bezierCurveTo(c,t,  7.173,-11.582,  12.581,-6.174, 12.583,0.500);
  bezierCurveTo(c,t,  12.581, 7.175,   7.173,12.583, 0.500,12.583);
  bezierCurveTo(c,t,  -6.175,12.583, -11.584,7.175, -11.584,0.500);
  bezierCurveTo(c,t,  -11.584,0.500,  -8.084,0.500,  -8.084,0.500);
  bezierCurveTo(c,t,  - 8.084,0.500,  -8.084,0.500,  -8.084,0.500);
  c.closePath();
}

function canvasEditContentPath(c,t) {
  moveToXY(c,t,15.067,2.250);
  bezierCurveTo(c,t, 9.088,2.250,  4.032,6.160,  2.289,11.559);
  lineToXY(c,t, 5.502,11.559);
  bezierCurveTo(c,t, 7.104,7.854, 10.773,5.258, 15.067,5.250);
  bezierCurveTo(c,t, 20.831,5.260, 25.495,9.924, 25.504,15.687);
  bezierCurveTo(c,t, 25.495,21.451, 20.831,26.115, 15.067,26.125);
  bezierCurveTo(c,t, 10.773,26.118, 7.103,23.520, 5.501,19.814);
  lineToXY(c,t, 2.289,19.814);
  bezierCurveTo(c,t, 4.033,25.213, 9.088,29.124, 15.068,29.126);
  bezierCurveTo(c,t, 22.487,29.124, 28.505,23.110, 28.506,15.688);
  bezierCurveTo(c,t, 28.504,8.265, 22.486,2.252, 15.067,2.250);
  c.closePath();

  moveToXY(c,t, 10.918,19.813);
  lineToXY(c,t, 18.068,15.687);
  lineToXY(c,t, 10.918,11.558);
  lineToXY(c,t, 10.918,13.855);
  lineToXY(c,t, -0.057,13.855);
  lineToXY(c,t, -0.057,17.516);
  lineToXY(c,t, 10.918,17.516);
  lineToXY(c,t, 10.918,19.813);
  c.closePath();
}



/** WEBPACK FOOTER **
 ** ./src/ui/tool/select-handles.js
 **/